import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import api, { formatApiErrorDetail, homeForUser } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, EnvelopeSimple, ChatTeardropDots, DeviceMobile, ShieldCheck, Lightning, WhatsappLogo } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import EmailOrPhoneInput from "@/components/EmailOrPhoneInput";
import VerificationChooser from "@/components/VerificationChooser";
import { useTranslation } from "react-i18next";
import EnvBadge from "@/components/EnvBadge";
import { isLiveMode } from "@/lib/envMode";
import { toast } from "sonner";

// Demo logins for the preview environment — every role the spec listed.
// Production hides this entire panel via isLiveMode() so live users
// never see seeded credentials. Update the matching `seed_data.py`
// entries whenever you add a new role here.
const DEMO_ACCOUNTS = [
  { roleKey: "super_admin",          email: "admin@factskoot.com",                pass: "Admin@123",   tone: "bg-terracotta text-white" },
  { roleKey: "hq_admin",             email: "hq@factskoot.com",                   pass: "Hq@12345",    tone: "bg-mustard text-ink" },
  { roleKey: "branch_admin",         email: "branch.bangalore@factskoot.com",     pass: "Branch@123",  tone: "bg-sage-deep text-white" },
  { roleKey: "principal",            email: "principal.bangalore@factskoot.com",  pass: "Staff@123",   tone: "bg-sage-deep text-white" },
  { roleKey: "academic_coordinator", email: "academic.bangalore@factskoot.com",   pass: "Staff@123",   tone: "bg-sage-deep text-white" },
  { roleKey: "counsellor",           email: "counsellor.bangalore@factskoot.com", pass: "Staff@123",   tone: "bg-lavender-deep text-white" },
  { roleKey: "teacher",              email: "teacher1@factskoot.com",             pass: "Teacher@123", tone: "bg-lavender-deep text-white" },
  { roleKey: "activity_trainer",     email: "activity.trainer.dance.bangalore@factskoot.com", pass: "Staff@123", tone: "bg-lavender-deep text-white" },
  { roleKey: "medical_coordinator",  email: "medical.bangalore@factskoot.com",    pass: "Staff@123",   tone: "bg-rose-400 text-white" },
  { roleKey: "non_teaching_staff",   email: "centre.manager.bangalore@factskoot.com", pass: "Staff@123", tone: "bg-mustard text-ink" },
  { roleKey: "technical_coordinator", email: "tech.coord.bangalore@factskoot.com", pass: "Staff@123",  tone: "bg-cobalt-light text-cobalt-deep" },
  { roleKey: "transport_staff",      email: "transport.bangalore@factskoot.com",  pass: "Staff@123",   tone: "bg-mustard text-ink" },
  { roleKey: "support_staff",        email: "support.bangalore@factskoot.com",    pass: "Staff@123",   tone: "bg-mustard text-ink" },
  { roleKey: "inventory_staff",      email: "inventory.bangalore@factskoot.com",  pass: "Staff@123",   tone: "bg-mustard text-ink" },
  { roleKey: "vendor",               email: "toytown@vendor.factskoot.com",       pass: "Vendor@123",  tone: "bg-paper-alt text-ink border border-border" },
  { roleKey: "father",               email: "parent1@example.com",                pass: "Parent@123",  tone: "bg-ink text-white" },
  { roleKey: "mother",               email: "parent30@example.com",               pass: "Parent@123",  tone: "bg-ink text-white" },
  { roleKey: "guardian",             email: "parent45@example.com",               pass: "Parent@123",  tone: "bg-ink text-white" },
  { roleKey: "auditor",              email: "auditor@factskoot.com",              pass: "Auditor@123", tone: "bg-paper-alt text-ink border border-border" },
];

export default function Login() {
  const { login, setSession } = useAuth();
  const { brand, isCoBranded } = useBranding();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const nav = navigate; // alias for splash redirect path consistency
  const location = useLocation();
  const [mode, setMode] = useState("password"); // password | otp
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otpChannel, setOtpChannel] = useState("whatsapp"); // email | whatsapp | sms
  const [otpStage, setOtpStage] = useState("send"); // send | verify
  const [otpCode, setOtpCode] = useState("");
  const [otpMaskedTo, setOtpMaskedTo] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onPasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(identifier, password);
      // Phase 5 — route through the welcome splash before the dashboard.
      // The splash auto-continues to the role-appropriate home in 10s
      // or whenever the user clicks "Enter MangoCubs".
      nav("/app/welcome", { replace: true, state: { next: homeForUser(u) } });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || t("errors.loginFailed"));
    } finally { setLoading(false); }
  };

  const onSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login-otp/send",
        { identifier, channel: otpChannel });
      setOtpMaskedTo(data.masked || identifier);
      setOtpStage("verify");
      toast.success(`OTP sent to ${data.masked || identifier}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login-otp/verify",
        { identifier, code: otpCode, channel: otpChannel });
      // Persist session via the existing AuthContext API.
      if (setSession) setSession(data.user, data.access_token);
      // Phase 5 — route through welcome splash on OTP login as well.
      nav("/app/welcome", { replace: true, state: { next: homeForUser(data.user) } });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  /** Called when <VerificationChooser> finishes a successful WhatsApp
   *  Scan & Verify. Exchange the verified session for a JWT login.
   *  Phase C — wires the universal verification component into login. */
  const onWhatsAppVerified = async (verified) => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login-via-session", {
        session_id: verified.session_id,
      });
      if (setSession) setSession(data.user, data.access_token);
      toast.success(`Welcome back, ${data.user?.name || "friend"}!`);
      nav("/app/welcome", { replace: true, state: { next: homeForUser(data.user) } });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) ||
              "Couldn't log you in. Your number may not be linked to a MangoCubs account — please ask your school to add you.");
    } finally { setLoading(false); }
  };

  const fillDemo = (acc) => {
    setIdentifier(acc.email);
    setPassword(acc.pass);
    setMode("password");
  };

  return (
    <div className="min-h-screen bg-paper grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col px-6 lg:px-16 py-10 justify-between">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-brand-link">
            <Logo size={42} tagline={true} />
          </Link>
          <EnvBadge />
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="my-12 max-w-md">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            {isCoBranded ? `${t("common.welcomeBack")} · ${brand.display_name}` : t("auth.loginTitle")}
          </h1>
          <p className="mt-3 text-ink-soft">
            {isCoBranded
              ? <>{t("auth.loginSub")} <span className="text-[10px] tracking-wider text-mustard-hover">Powered by MangoCubs</span></>
              : t("auth.loginSub")}
          </p>

          {/* Login mode toggle */}
          <div className="mt-6 inline-flex rounded-full bg-paper-alt p-1 text-xs font-semibold" data-testid="login-mode-toggle">
            <button onClick={() => { setMode("password"); setErr(""); setOtpStage("send"); }}
              className={`px-4 py-1.5 rounded-full transition ${mode === "password" ? "bg-white text-ink shadow-soft" : "text-ink-soft"}`}
              data-testid="login-mode-password">
              <ShieldCheck weight={mode === "password" ? "fill" : "duotone"} className="inline mr-1" /> Password
            </button>
            <button onClick={() => { setMode("otp"); setErr(""); }}
              className={`px-4 py-1.5 rounded-full transition ${mode === "otp" ? "bg-white text-ink shadow-soft" : "text-ink-soft"}`}
              data-testid="login-mode-otp">
              <Lightning weight={mode === "otp" ? "fill" : "duotone"} className="inline mr-1" /> One-time code
            </button>
            <button onClick={() => { setMode("whatsapp"); setErr(""); }}
              className={`px-4 py-1.5 rounded-full transition ${mode === "whatsapp" ? "bg-white text-emerald-700 shadow-soft" : "text-ink-soft"}`}
              data-testid="login-mode-whatsapp">
              <WhatsappLogo weight={mode === "whatsapp" ? "fill" : "duotone"} className="inline mr-1" /> WhatsApp
            </button>
          </div>

          {mode === "password" ? (            <form onSubmit={onPasswordLogin} className="mt-5 space-y-4" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email or mobile</label>
                <EmailOrPhoneInput
                  value={identifier}
                  onChange={setIdentifier}
                  placeholder="you@school.com or start typing a number…"
                  testId="login-email-input"
                  autoFocus
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-ink">{t("auth.passwordLabel")}</label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-terracotta hover:underline" data-testid="login-forgot-link">
                    Forgot password?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta"
                  data-testid="login-password-input"
                />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover transition disabled:opacity-60 flex items-center justify-center gap-2 shadow-floating"
                data-testid="login-submit-btn"
              >
                {loading ? t("auth.loggingIn") : (<>{t("auth.signIn")} <ArrowRight weight="bold" /></>)}
              </button>
            </form>
          ) : mode === "whatsapp" ? (
            // WhatsApp Scan & Verify login (Phase C) — uses the universal
            // <VerificationChooser> so we get the responsive QR-vs-button
            // behaviour, polling, 10-min timer, regenerate, and an
            // explanatory "manual mode" banner for free.
            <div className="mt-5 space-y-4" data-testid="login-whatsapp-panel">
              <div className="text-sm text-ink-soft">
                Sign in by sending a quick WhatsApp message to MangoCubs —
                no password, no OTP to type. Your phone confirms it&apos;s you.
              </div>
              <VerificationChooser
                purpose="login"
                allowedChannels={["whatsapp_scan"]}
                defaultChannel="whatsapp_scan"
                onVerified={onWhatsAppVerified}
              />
              {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
              <p className="text-xs text-ink-soft text-center">
                Prefer email or SMS instead?{" "}
                <button type="button" onClick={() => { setMode("otp"); setErr(""); }}
                  className="text-terracotta font-semibold hover:underline"
                  data-testid="login-whatsapp-switch-otp">
                  Use a one-time code
                </button>
              </p>
            </div>
          ) : (
            // OTP login mode (Phase 4)
            <form onSubmit={otpStage === "send" ? onSendOtp : onVerifyOtp}
              className="mt-5 space-y-4" data-testid="login-otp-form">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email or mobile</label>
                <EmailOrPhoneInput
                  value={identifier}
                  onChange={setIdentifier}
                  placeholder="you@school.com or start typing a number…"
                  testId="login-otp-identifier"
                  disabled={otpStage === "verify"}
                />
              </div>
              {otpStage === "send" && (
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Send code via</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="login-otp-channel">
                    {[
                      { v: "whatsapp", label: "WhatsApp", icon: ChatTeardropDots },
                      { v: "email", label: "Email", icon: EnvelopeSimple },
                      { v: "sms", label: "SMS", icon: DeviceMobile },
                    ].map((c) => (
                      <button type="button" key={c.v}
                        onClick={() => setOtpChannel(c.v)}
                        data-testid={`login-otp-channel-${c.v}`}
                        className={`h-12 rounded-xl border text-xs font-semibold flex flex-col items-center justify-center gap-0.5 transition ${
                          otpChannel === c.v ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border bg-white text-ink-soft hover:border-terracotta/50"
                        }`}>
                        <c.icon weight={otpChannel === c.v ? "fill" : "duotone"} size={18} />
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {otpStage === "verify" && (
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Enter the 6-digit code</label>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                    placeholder="••••••"
                    className="w-full h-14 px-4 rounded-xl border border-border bg-white text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-terracotta"
                    data-testid="login-otp-code"
                  />
                  <p className="text-xs text-ink-soft mt-1.5">
                    Code sent to <span className="font-mono">{otpMaskedTo}</span>.
                    <button type="button" onClick={() => { setOtpStage("send"); setOtpCode(""); }}
                      className="text-terracotta font-semibold ml-1 hover:underline" data-testid="login-otp-resend">
                      Resend / change channel
                    </button>
                  </p>
                </div>
              )}
              {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={loading || (otpStage === "verify" && otpCode.length !== 6)}
                className="w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover transition disabled:opacity-60 flex items-center justify-center gap-2 shadow-floating"
                data-testid="login-otp-submit-btn">
                {loading
                  ? (otpStage === "send" ? "Sending code…" : "Verifying…")
                  : (otpStage === "send"
                      ? (<>Send code <ArrowRight weight="bold" /></>)
                      : (<>Verify & sign in <ArrowRight weight="bold" /></>))}
              </button>
            </form>
          )}

          <p className="text-sm text-ink-soft mt-6 text-center">
            {t("auth.noAccount")} <Link to="/register" className="text-terracotta font-semibold hover:underline" data-testid="login-register-link">{t("auth.createAccount")}</Link>
          </p>
        </motion.div>

        <div className="text-xs text-ink-soft">
          © {new Date().getFullYear()} {isCoBranded ? brand.display_name : "MangoCubs"}
          {isCoBranded && <span className="ml-2 text-[10px] tracking-wider text-mustard-hover">Powered by MangoCubs</span>}
        </div>
      </div>

      {/* Right: demo cards — only shown on TEST / preview environments.
          Production (mangocubs.com) hides this panel entirely so live users
          never see demo accounts or pre-filled passwords. */}
      {!isLiveMode() && (
      <div className="hidden lg:flex flex-col justify-center bg-paper-alt px-12 py-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-mustard/30 blur-3xl" />
        <div className="relative z-10 max-w-lg">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">{t("auth.demoWorkspace")}</div>
          <h2 className="font-heading text-3xl font-bold mt-2">{t("auth.demoH2")}</h2>
          <p className="text-ink-soft mt-2">{t("auth.demoSub")}</p>
          <div className="mt-8 space-y-3" data-testid="demo-account-list">
            {DEMO_ACCOUNTS.map((acc) => (
              <button
                key={acc.roleKey}
                onClick={() => fillDemo(acc)}
                className="w-full text-left p-4 rounded-2xl bg-white border border-border hover:shadow-hoverlift transition group"
                data-testid={`demo-acc-${acc.roleKey}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${acc.tone}`}>{t(`roles.${acc.roleKey}`)}</div>
                    <div className="font-medium mt-2 text-sm">{acc.email}</div>
                    <div className="text-xs text-ink-soft mt-0.5">{t("auth.passwordLabel")}: <span className="font-mono">{acc.pass}</span></div>
                  </div>
                  <ArrowRight className="text-ink-soft group-hover:text-terracotta transition" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
